"use client";

import { Music2, Pause, Play, X } from "lucide-react";
import type { MusicAttachment } from "@/lib/music/attachment";
import { musicArtworkSrc } from "@/lib/music/links";
import { cn } from "@/lib/utils";
import { PlayingBars } from "./playing-bars";
import { useMusicPreview } from "./use-music-preview";

/**
 * The picked song inside a composer's attachment tray: artwork that previews
 * on tap, title · artist, and remove. Same height as the location/event tags.
 */
export function MusicAttachmentChip({
  music,
  onRemove,
  onChange,
  className,
}: {
  music: MusicAttachment;
  onRemove: () => void;
  onChange?: () => void;
  className?: string;
}) {
  const preview = useMusicPreview();
  const playing =
    preview.key === music.songId &&
    (preview.status === "playing" || preview.status === "loading");
  const art = musicArtworkSrc(music.artworkUrl, 64);

  return (
    <div
      className={cn(
        "flex h-11 w-fit max-w-full items-center gap-2 rounded-full border border-border bg-surface-alt py-1 pr-1 pl-1 dark:border-glass-border-10 dark:bg-glass-bg-05",
        className,
      )}
    >
      <button
        type="button"
        disabled={!music.previewUrl}
        onClick={() =>
          preview.toggle({
            key: music.songId,
            previewUrl: music.previewUrl,
            surface: "post",
          })
        }
        aria-label={playing ? "Pause preview" : "Play preview"}
        className="relative size-9 shrink-0 overflow-hidden rounded-full bg-surface"
      >
        {art ? (
          // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
          <img src={art} alt="" className="h-full w-full object-cover" />
        ) : (
          <Music2 className="m-auto size-4 text-text-tertiary" />
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/35 text-white">
          {playing ? (
            <Pause className="size-3.5" fill="currentColor" strokeWidth={0} />
          ) : (
            <Play className="size-3.5" fill="currentColor" strokeWidth={0} />
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={onChange}
        disabled={!onChange}
        className="flex min-w-0 flex-col text-left leading-tight disabled:cursor-default"
      >
        <span className="flex items-center gap-1 truncate text-xs font-semibold text-text-primary">
          {playing ? (
            <PlayingBars className="h-2.5 text-brand" barClassName="w-[2px]" />
          ) : (
            <Music2 className="size-3 shrink-0 text-brand" />
          )}
          <span className="truncate">{music.title}</span>
        </span>
        <span className="truncate text-[11px] text-text-secondary">
          {music.artistName}
        </span>
      </button>
      <button
        type="button"
        onClick={() => {
          preview.pause();
          onRemove();
        }}
        aria-label="Remove song"
        className="pointer-fine:hover:bg-surface-hover flex size-7 shrink-0 items-center justify-center rounded-full text-text-tertiary"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
